import { c as _c } from "react/compiler-runtime";
import type { PropsWithChildren } from 'react';
import classnames from 'classnames';
import type { CategoryID, ItemAdvertiser } from '@sbt-web/network/types';
import { Badge, Divider } from '@sbt-web/ui';
import { getShopLabelOfCategoryID } from '@sbt-web/category-utils';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import { UserRating } from '@/client/components/Detail/UserRating';
import { PrivateUserBadgeSize } from '@/client/components/Detail/PrivateUserProfileBadge';
import { DEFAULT_ADVERTISER_NAME } from '@/shared/constants';
import { getAdvertiserName, isPro } from '@/shared/models/Advertiser';
import { AchievementBadges } from '@/shared/components/AchievementBadges';
import { PrivateUserAvatar } from '../PrivateUser/Avatar';
import { PrivateUserName } from '../PrivateUser/Name';
import { PrivateUserContact } from '../PrivateUser/Contact';
import { ContactButton } from '../ContactUser';
import { ShopGoogleReviews } from '../ShopGoogleReviews';
import { ShopLogo } from './ShopLogo';
import type { UserDetailsProps } from './types';
import { getShopUrl } from './utils/getShopUrl';
import ownClasses from './index.module.scss';
export const CompanyBadge = t0 => {
  const $ = _c(4);
  const {
    categoryId
  } = t0;
  let t1;
  if ($[0] !== categoryId) {
    t1 = getShopLabelOfCategoryID(categoryId);
    $[0] = categoryId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  let t2;
  if ($[2] !== label) {
    t2 = label ? <Badge classes={[ownClasses["company-badge"]]}>{label}</Badge> : null;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
type ShopLinkWrapper = PropsWithChildren<{
  advertiser: ItemAdvertiser;
}>;
const ShopLinkWrapper = t0 => {
  const $ = _c(6);
  const {
    children,
    advertiser
  } = t0;
  const {
    shopName,
    shopId
  } = advertiser;
  let t1;
  if ($[0] !== shopId || $[1] !== shopName) {
    t1 = getShopUrl(shopId, shopName);
    $[0] = shopId;
    $[1] = shopName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shopUrl = t1;
  let t2;
  if ($[3] !== children || $[4] !== shopUrl) {
    t2 = shopUrl ? <a href={shopUrl} className={classnames(ownClasses["shop-link"])}>{children}</a> : <>{children}</>;
    $[3] = children;
    $[4] = shopUrl;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
type ShopWrapperProps = PropsWithChildren<{
  advertiser: ItemAdvertiser;
  showContactButton: boolean;
}>;
const ShopWrapper = t0 => {
  const $ = _c(7);
  const {
    children,
    advertiser,
    showContactButton
  } = t0;
  const {
    shopName,
    shopId
  } = advertiser;
  let t1;
  if ($[0] !== shopId || $[1] !== shopName) {
    t1 = getShopUrl(shopId, shopName);
    $[0] = shopId;
    $[1] = shopName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shopUrl = t1;
  let t2;
  if ($[3] !== children || $[4] !== shopUrl || $[5] !== showContactButton) {
    t2 = shopUrl ? <div className={classnames(ownClasses["shop-details-wrapper"])}>{children}</div> : <div className={ownClasses["shop-with-contact"]}><div className={ownClasses["shop-details-wrapper"]}>{children}</div>{showContactButton && <ContactButton />}</div>;
    $[3] = children;
    $[4] = shopUrl;
    $[5] = showContactButton;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
export const UserDetails = t0 => {
  const $ = _c(60);
  const {
    advertiser,
    categoryId,
    advertiserProfile,
    shop,
    shopGoogleReviews,
    trustInfo,
    showContactButton,
    privateUserInfo
  } = t0;
  if (isPro(advertiser.type)) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Divider spacing="md" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    const t2 = showContactButton || false;
    const t3 = shop?.logo?.medium?.secureuri;
    let t4;
    if ($[1] !== t3) {
      t4 = <ShopLogo logoURL={t3} />;
      $[1] = t3;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] !== advertiser || $[4] !== t4) {
      t5 = <ShopLinkWrapper advertiser={advertiser}>{t4}</ShopLinkWrapper>;
      $[3] = advertiser;
      $[4] = t4;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== categoryId) {
      t6 = <CompanyBadge categoryId={categoryId} />;
      $[6] = categoryId;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = classnames("subheading", ownClasses["user-name"]);
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    let t8;
    if ($[9] !== advertiser.type || $[10] !== advertiserProfile || $[11] !== shop) {
      t8 = getAdvertiserName(advertiser.type, shop, advertiserProfile);
      $[9] = advertiser.type;
      $[10] = advertiserProfile;
      $[11] = shop;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] !== t8) {
      t9 = <p className={t7}>{t8}</p>;
      $[13] = t8;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    let t10;
    if ($[15] !== advertiser || $[16] !== t9) {
      t10 = <ShopLinkWrapper advertiser={advertiser}>{t9}</ShopLinkWrapper>;
      $[15] = advertiser;
      $[16] = t9;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] !== shopGoogleReviews) {
      t11 = <ShopGoogleReviews data={shopGoogleReviews} />;
      $[18] = shopGoogleReviews;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    let t12;
    if ($[20] !== t10 || $[21] !== t11 || $[22] !== t6) {
      t12 = <div className={ownClasses["user-details-name"]}>{t6}{t10}{t11}</div>;
      $[20] = t10;
      $[21] = t11;
      $[22] = t6;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    let t13;
    if ($[24] !== advertiser || $[25] !== t12 || $[26] !== t2 || $[27] !== t5) {
      t13 = <>{t1}<ShopWrapper advertiser={advertiser} showContactButton={t2}>{t5}{t12}</ShopWrapper></>;
      $[24] = advertiser;
      $[25] = t12;
      $[26] = t2;
      $[27] = t5;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    return t13;
  }
  const t1 = advertiserProfile?.username ?? DEFAULT_ADVERTISER_NAME;
  const t2 = advertiserProfile?.picture;
  const t3 = trustInfo?.presence.enabled ? trustInfo?.presence.status ?? undefined : undefined;
  let t4;
  if ($[29] !== advertiser.userId || $[30] !== t1 || $[31] !== t2 || $[32] !== t3) {
    t4 = <PrivateUserAvatar userId={advertiser.userId} name={t1} pulseEventName="click-on-name-advertiser-ad-detail" picture={t2} size={PrivateUserBadgeSize.Small} presenceStatus={t3} />;
    $[29] = advertiser.userId;
    $[30] = t1;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  const t5 = advertiserProfile?.username ?? DEFAULT_ADVERTISER_NAME;
  let t6;
  if ($[34] !== advertiser.userId || $[35] !== t5) {
    t6 = <PrivateUserName userId={advertiser.userId} name={t5} pulseEventName="click-on-name-advertiser-ad-detail" />;
    $[34] = advertiser.userId;
    $[35] = t5;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  const t7 = advertiser.userId;
  let t8;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = getOrCreatePulse();
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  const t9 = trustInfo?.reputation.feedback?.sourceScores.MEMBER;
  const t10 = trustInfo?.reputation.feedback?.sourceCounts?.MEMBER;
  let t11;
  if ($[38] !== advertiser.userId || $[39] !== t10 || $[40] !== t9) {
    t11 = <UserRating userId={t7} pulseInstance={t8} score={t9} reviewCount={t10} />;
    $[38] = advertiser.userId;
    $[39] = t10;
    $[40] = t9;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== t11 || $[43] !== t6) {
    t12 = <div className={ownClasses["private-user-column"]}>{t6}{t11}</div>;
    $[42] = t11;
    $[43] = t6;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== t12 || $[46] !== t4) {
    t13 = <div className={ownClasses["private-user-info"]}>{t4}{t12}</div>;
    $[45] = t12;
    $[46] = t4;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  const t14 = privateUserInfo?.badges;
  let t15;
  if ($[48] !== categoryId || $[49] !== t14) {
    t15 = <AchievementBadges categoryId={categoryId} data={t14} />;
    $[48] = categoryId;
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== showContactButton) {
    t16 = showContactButton && <PrivateUserContact className={ownClasses["private-user-contact"]} />;
    $[51] = showContactButton;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== t13 || $[54] !== t15 || $[55] !== t16) {
    t17 = <div className={ownClasses["private-user-container"]}>{t13}{t15}{t16}</div>;
    $[53] = t13;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Divider topSpacing="md" bottomSpacing="null" />;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== t17) {
    t19 = <>{t17}{t18}</>;
    $[58] = t17;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  return t19;
};
export default UserDetails;